import { FC, useEffect, useMemo, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { $CurrentHashtagInfoAsAvailable } from '@hashtag/shared/current-hashtag-info';
import { useTranslation } from 'react-i18next';
import { useNumFormatter } from '@lemon8/web-app-shared/hook';
import _throttle from 'lodash/throttle';
import _clamp from 'lodash/clamp';
import { $CurrentHashtagPopularPosts } from '@hashtag/shared/current-hashtag-post';
import { ArticleDetailUtil } from 'shared/models/article';

import './style.scss';

export const HashtagInfo: FC = () => {
  const { t } = useTranslation();
  const formatNum = useNumFormatter();
  const hashtag = useAtomValue($CurrentHashtagInfoAsAvailable);
  const { items: hashtagPopularPosts } = useAtomValue($CurrentHashtagPopularPosts);

  const fixedTitleRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const cover = useMemo(() => {
    const backgroundUrl = hashtag.background?.url;
    if (backgroundUrl) {
      return backgroundUrl;
    }

    if (hashtagPopularPosts.length) {
      return ArticleDetailUtil.getCoverImage(hashtagPopularPosts[0]).url;
    }

    return null;
  }, []);

  useEffect(() => {
    if (!fixedTitleRef.current || !titleRef.current) {
      return;
    }

    const fixedTitle = fixedTitleRef.current;
    const title = titleRef.current;

    const callback = _throttle(() => {
      const fixedTitleRect = fixedTitle.getBoundingClientRect();
      const titleRect = title.getBoundingClientRect();

      const opacity = _clamp((fixedTitleRect.top - titleRect.top - 24) / (titleRect.bottom - titleRect.top), 0, 1);
      fixedTitle.style.opacity = `${opacity}`;
    }, 16);

    window.addEventListener('scroll', callback);

    return () => {
      window.removeEventListener('scroll', callback);
    };
  }, []);

  return (
    <>
      <div className="hashtag-fixed-title">
        <div className="hashtag-fixed-title-content" ref={fixedTitleRef}>
          <span>#{hashtag.name}</span>
        </div>
      </div>

      <div className="hashtag-info">
        {cover && (
          <div className="hashtag-info-top">
            <img loading="eager" src={cover} />
          </div>
        )}

        <div className="hashtag-info-content">
          <div className="hashtag-info-content-title" ref={titleRef}>
            <span>#{hashtag.name}</span>
          </div>

          <div className="hashtag-info-content-footer">
            <span className="hashtag-info-content-footer-number">
              <span className="hashtag-info-content-footer-word">
                {t('view_count_plural', { num: hashtag.viewsCount }).replace('%d', formatNum(hashtag.viewsCount))}
              </span>
            </span>
          </div>

          {Boolean(hashtag.description) && <div className="hashtag-info-content-desc">{hashtag.description}</div>}
        </div>
      </div>
    </>
  );
};
